import React from "react";
import { Badge } from "../../../../../ui/components/Badge";
import { Table } from "../../../../../ui/components/Table";
import { Button } from "../../../../../ui/components/Button";
import { FeatherChevronLeft, FeatherChevronRight } from "@subframe/core";
import { useReconciliationStatus } from "../../hooks/useReconciliationStatus";
import type { ReconciliationStatusItemEntity } from "../../../domain/entities/ReconciliationStatusEntity";
import { Skeleton } from "../shared";

function unmatchedVariant(
  count: number,
): "success" | "warning" | "error" | "neutral" {
  if (count === 0) return "success";
  if (count > 10) return "error";
  return "warning";
}

export function ReconciliationStatusSection() {
  const { items, loading, error, pagination, setPage } =
    useReconciliationStatus();

  const {
    total: totalItems,
    limit: itemsPerPage,
    page: currentPage,
    totalPages,
    hasMore,
  } = pagination;

  const sortedItems = React.useMemo(() => {
    const priority = (t: ReconciliationStatusItemEntity["accountType"]) =>
      t === "bank" ? 0 : 1;

    return items
      .map((item, idx) => ({ item, idx }))
      .sort((a, b) => {
        const byType = priority(a.item.accountType) - priority(b.item.accountType);
        return byType !== 0 ? byType : a.idx - b.idx; // stable
      })
      .map(({ item }) => item);
  }, [items]);

  return (
    <div className="flex w-full flex-col items-start gap-6 border border-solid border-neutral-border bg-default-background px-6 py-6 shadow-md">
      <div className="flex w-full flex-col items-start gap-2">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Reconciliation Status
        </span>
        <span className="text-caption font-caption text-subtext-color">
          How closely your books match your bank statements.
        </span>
      </div>
      {error && <p className="text-body font-body text-error-700">{error}</p>}
      {loading && items.length === 0 ? (
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full flex-col items-start gap-4 overflow-hidden flex-shrink-0 overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>ACCOUNT</Table.HeaderCell>
                  <Table.HeaderCell>LAST RECONCILED PERIOD</Table.HeaderCell>
                  <Table.HeaderCell>LAST RECONCILED ON</Table.HeaderCell>
                  <Table.HeaderCell>UNMATCHED TRANSACTIONS</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <Table.Row key={i}>
                  <Table.Cell>
                    <Skeleton className="h-4 w-40 rounded" />
                  </Table.Cell>
                  <Table.Cell>
                    <Skeleton className="h-4 w-24 rounded" />
                  </Table.Cell>
                  <Table.Cell>
                    <Skeleton className="h-4 w-28 rounded" />
                  </Table.Cell>
                  <Table.Cell>
                    <Skeleton className="h-6 w-10 rounded-full" />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          </div>
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col items-start gap-4 overflow-hidden flex-shrink-0 overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>ACCOUNT</Table.HeaderCell>
                  {/* <Table.HeaderCell>LAST RECONCILED PERIOD</Table.HeaderCell> */}
                  <Table.HeaderCell>LAST RECONCILED ON</Table.HeaderCell>
                  <Table.HeaderCell>UNMATCHED TRANSACTIONS</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {sortedItems.map((row: ReconciliationStatusItemEntity, i: number) =>
                (() => {
                  const unmatchedCount =
                    typeof row.unmatchedCount === "number" &&
                    Number.isFinite(row.unmatchedCount)
                      ? row.unmatchedCount
                      : 0;

                  return (
                    <Table.Row key={`${row.accountName}-${i}`}>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {row.accountName}
                        </span>
                      </Table.Cell>
                      {/* <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {row.lastReconciledPeriod || "—"}
                        </span>
                      </Table.Cell> */}
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {row.lastReconciledOn || "—"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant={unmatchedVariant(unmatchedCount)}>
                          {String(unmatchedCount)}
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  );
                })(),
              )}
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex w-full items-center justify-between mt-4">
              <span className="text-body font-body text-subtext-color">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} results
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  icon={<FeatherChevronLeft />}
                >
                  Previous
                </Button>
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={!hasMore}
                  icon={<FeatherChevronRight />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
